import React, { useState } from 'react';
import { Tabs, Tab, Box, Typography } from '@mui/material';
import { useAppSelector } from '../../../../../app/store';
import BaseRatesTab from '../../tabs/contractTabs/BaseRatesTab';
import CodesTab from '../../tabs/contractTabs/CodesTab';
import SummaryTab from '../../tabs/contractTabs/SummaryTab';
import CptCodesTab from '../../tabs/contractTabs/CptCodesTab';
import CptGroupersTab from '../../tabs/contractTabs/CptGroupersTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`fee-schedule-tabpanel-${index}`}
      aria-labelledby={`fee-schedule-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `fee-schedule-tab-${index}`,
    'aria-controls': `fee-schedule-tabpanel-${index}`,
  };
};

const FeeScheduleTabStrip: React.FC = () => {
  const [value, setValue] = useState(0);
  const selectedFeeSchedule = useAppSelector((state) => state.contract.currentFeeSchedule);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Determine which tabs to show based on fee type and fee sub type
  const getTabs = () => {
    const feeType = selectedFeeSchedule?.feeType?.toLowerCase();
    const feeSubType = selectedFeeSchedule?.feeSubType?.toLowerCase();

    if (feeType === 'drg') {
      return [
        { label: 'Summary', content: <SummaryTab /> },
        { label: 'Codes', content: <CodesTab /> },
        { label: 'Base Rates', content: <BaseRatesTab /> }
      ];
    } else if (feeType === 'cpt') {
      const tabs = [
        { label: 'Summary', content: <SummaryTab /> },
        { label: 'CPT Codes', content: <CptCodesTab /> }
      ];

      // Add Groupers tab if fee sub type is "Grouped"
      if (feeSubType === 'grouped') {
        tabs.push({ label: 'Groupers', content: <CptGroupersTab /> });
      }

      return tabs;
    }

    return [];
  };

  const tabs = getTabs();

  // Don't render anything if no tabs are available
  if (tabs.length === 0) {
    return null;
  }

  // Reset tab value if it's out of bounds
  if (value >= tabs.length) {
    setValue(0);
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange} aria-label="fee schedule tabs">
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} {...a11yProps(index)} />
          ))}
        </Tabs>
      </Box>
      {tabs.map((tab, index) => (
        <TabPanel key={index} value={value} index={index}>
          {tab.content}
        </TabPanel>
      ))}
    </Box>
  );
};

export default FeeScheduleTabStrip;
